import { useEffect, useRef, useState } from 'react';
import { useEditorStore } from '../../state/editorStore';
import { PanelShell } from '../controls/PanelShell';

type ScopeKind = 'parade' | 'waveform' | 'vectorscope' | 'histogram';

/** Shared viewer sample — one readback for all scope panes. */
let sharedSample: { data: Uint8ClampedArray; w: number; h: number } | null = null;
let sharedSampleAt = 0;
const SAMPLE_INTERVAL_MS = 120;
const SAMPLE_W = 96;
const SAMPLE_H = 54;

function getSharedViewerPixels(): { data: Uint8ClampedArray; w: number; h: number } | null {
  const now = performance.now();
  if (sharedSample && now - sharedSampleAt < SAMPLE_INTERVAL_MS) return sharedSample;

  const root = document.getElementById('cinegrid-viewer');
  const src = root?.querySelector('canvas');
  if (!src || src.width < 2 || src.height < 2) {
    sharedSample = null;
    sharedSampleAt = now;
    return null;
  }
  const scale = Math.min(1, SAMPLE_W / src.width, SAMPLE_H / src.height);
  const w = Math.max(1, Math.floor(src.width * scale));
  const h = Math.max(1, Math.floor(src.height * scale));
  const tmp = document.createElement('canvas');
  tmp.width = w;
  tmp.height = h;
  const ctx = tmp.getContext('2d', { willReadFrequently: true });
  if (!ctx) return sharedSample;
  try {
    ctx.drawImage(src, 0, 0, w, h);
    const img = ctx.getImageData(0, 0, w, h);
    sharedSample = { data: img.data, w, h };
    sharedSampleAt = now;
    return sharedSample;
  } catch {
    return sharedSample;
  }
}

function drawGrid(ctx: CanvasRenderingContext2D, w: number, h: number, labels: number[]) {
  const hair =
    getComputedStyle(document.documentElement).getPropertyValue('--cg-border').trim() ||
    '#2e3339';
  const text =
    getComputedStyle(document.documentElement).getPropertyValue('--cg-text-low').trim() ||
    '#8a9098';
  ctx.strokeStyle = hair;
  ctx.fillStyle = text;
  ctx.font = '9px ui-sans-serif, system-ui, sans-serif';
  ctx.lineWidth = 1;
  for (const v of labels) {
    const t = v / 1023;
    const y = h - 4 - t * (h - 12);
    ctx.globalAlpha = v === 512 ? 0.7 : 0.35;
    ctx.beginPath();
    ctx.moveTo(28, y);
    ctx.lineTo(w - 4, y);
    ctx.stroke();
    ctx.globalAlpha = 0.9;
    ctx.textAlign = 'right';
    ctx.fillText(String(v), 26, y + 3);
  }
  ctx.globalAlpha = 1;
}

function blitAcc(
  ctx: CanvasRenderingContext2D,
  acc: Float32Array,
  cw: number,
  ch: number,
  ox: number,
  oy: number,
  rgb: [number, number, number]
) {
  let max = 1;
  for (let i = 0; i < acc.length; i++) max = Math.max(max, acc[i]!);
  const img = ctx.createImageData(cw, ch);
  for (let i = 0; i < acc.length; i++) {
    const a = Math.min(255, Math.floor((acc[i]! / max) * 255 * 1.6));
    const o = i * 4;
    img.data[o] = rgb[0];
    img.data[o + 1] = rgb[1];
    img.data[o + 2] = rgb[2];
    img.data[o + 3] = a;
  }
  const tmp = document.createElement('canvas');
  tmp.width = cw;
  tmp.height = ch;
  tmp.getContext('2d')!.putImageData(img, 0, 0);
  ctx.drawImage(tmp, ox, oy);
}

function ScopeCanvas({ title, kind }: { title: string; kind: ScopeKind }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isPlaying = useEditorStore((s) => s.isPlaying);
  const isLeader = useEditorStore((s) => s.isLeader);
  const connectedTabs = useEditorStore((s) => s.connectedTabs);
  const dualFollower = !isLeader && connectedTabs.length > 1;

  useEffect(() => {
    let alive = true;
    let raf = 0;
    let lastPaint = 0;
    // Dual Color Suite: rare samples so scopes don't look frozen but stay light
    const interval = dualFollower && isPlaying ? 500 : isPlaying ? 280 : 140;

    const paint = (now: number) => {
      if (!alive) return;
      if (now - lastPaint < interval) {
        raf = requestAnimationFrame(paint);
        return;
      }
      lastPaint = now;

      const canvas = canvasRef.current;
      if (!canvas) {
        raf = requestAnimationFrame(paint);
        return;
      }
      const dpr = Math.min(1.5, window.devicePixelRatio || 1);
      const cssW = canvas.clientWidth || 320;
      const cssH = canvas.clientHeight || 160;
      if (canvas.width !== Math.floor(cssW * dpr) || canvas.height !== Math.floor(cssH * dpr)) {
        canvas.width = Math.floor(cssW * dpr);
        canvas.height = Math.floor(cssH * dpr);
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        raf = requestAnimationFrame(paint);
        return;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const w = cssW;
      const h = cssH;
      const bg =
        getComputedStyle(document.documentElement).getPropertyValue('--cg-bg-base').trim() ||
        '#0a0b0c';
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      const pixels = getSharedViewerPixels();
      const labels = [0, 256, 512, 768, 1023];

      if (!pixels) {
        ctx.fillStyle =
          getComputedStyle(document.documentElement).getPropertyValue('--cg-text-low').trim() ||
          '#8a9098';
        ctx.font = '10px ui-sans-serif, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Waiting for viewer…', w / 2, h / 2);
        raf = requestAnimationFrame(paint);
        return;
      }

      const { data, w: iw, h: ih } = pixels;

      if (kind === 'histogram') {
        const binsR = new Float32Array(128);
        const binsG = new Float32Array(128);
        const binsB = new Float32Array(128);
        for (let i = 0; i < data.length; i += 4) {
          binsR[data[i]! >> 1]++;
          binsG[data[i + 1]! >> 1]++;
          binsB[data[i + 2]! >> 1]++;
        }
        let max = 1;
        for (let i = 0; i < 128; i++) max = Math.max(max, binsR[i]!, binsG[i]!, binsB[i]!);
        const rowH = (h - 8) / 3;
        const drawBins = (bins: Float32Array, y0: number, color: string) => {
          ctx.fillStyle = color;
          const barW = (w - 8) / 128;
          for (let i = 0; i < 128; i++) {
            const bh = (bins[i]! / max) * (rowH - 4);
            ctx.fillRect(4 + i * barW, y0 + rowH - bh, Math.ceil(barW), bh);
          }
        };
        drawBins(binsR, 4, 'rgba(255,80,80,0.85)');
        drawBins(binsG, 4 + rowH, 'rgba(80,220,100,0.85)');
        drawBins(binsB, 4 + rowH * 2, 'rgba(80,140,255,0.85)');
      } else if (kind === 'parade' || kind === 'waveform') {
        drawGrid(ctx, w, h, labels);
        const left = 30;
        const plotW = w - left - 6;
        const plotH = Math.max(1, Math.floor(h - 10));
        const cols = kind === 'parade' ? 3 : 1;
        const colW = Math.max(1, Math.floor(plotW / cols));
        const accR = new Float32Array(colW * plotH);
        const accG = new Float32Array(accR.length);
        const accB = new Float32Array(accR.length);

        // Sparse sample for speed
        for (let y = 0; y < ih; y += 1) {
          for (let x = 0; x < iw; x += 1) {
            const i = (y * iw + x) * 4;
            const r = data[i]! / 255;
            const g = data[i + 1]! / 255;
            const b = data[i + 2]! / 255;
            const nx = x / iw;
            const px = Math.min(colW - 1, Math.floor(nx * colW));
            const put = (ch: number, acc: Float32Array) => {
              const py = Math.min(plotH - 1, Math.floor((1 - ch) * plotH));
              acc[py * colW + px]! += 1;
            };
            put(r, accR);
            put(g, accG);
            put(b, accB);
          }
        }

        if (kind === 'parade') {
          blitAcc(ctx, accR, colW, plotH, left, 4, [255, 70, 70]);
          blitAcc(ctx, accG, colW, plotH, left + colW, 4, [70, 210, 90]);
          blitAcc(ctx, accB, colW, plotH, left + colW * 2, 4, [70, 130, 255]);
        } else {
          blitAcc(ctx, accR, colW, plotH, left, 4, [255, 70, 70]);
          blitAcc(ctx, accG, colW, plotH, left, 4, [70, 210, 90]);
          blitAcc(ctx, accB, colW, plotH, left, 4, [70, 130, 255]);
        }
      } else if (kind === 'vectorscope') {
        const cx = w / 2;
        const cy = h / 2;
        const rad = Math.min(w, h) * 0.42;
        ctx.strokeStyle =
          getComputedStyle(document.documentElement).getPropertyValue('--cg-border').trim() ||
          '#3d444c';
        ctx.beginPath();
        ctx.arc(cx, cy, rad, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(cx - rad, cy);
        ctx.lineTo(cx + rad, cy);
        ctx.moveTo(cx, cy - rad);
        ctx.lineTo(cx, cy + rad);
        ctx.stroke();

        const targets: { label: string; ang: number }[] = [
          { label: 'R', ang: -15 },
          { label: 'Y', ang: 45 },
          { label: 'G', ang: 105 },
          { label: 'C', ang: 165 },
          { label: 'B', ang: 225 },
          { label: 'M', ang: 285 },
        ];
        ctx.fillStyle =
          getComputedStyle(document.documentElement).getPropertyValue('--cg-accent').trim() ||
          '#e8a33d';
        ctx.font = '9px ui-sans-serif, system-ui, sans-serif';
        for (const t of targets) {
          const a = (t.ang * Math.PI) / 180;
          const x = cx + Math.cos(a) * rad * 0.78;
          const y = cy + Math.sin(a) * rad * 0.78;
          ctx.strokeRect(x - 4, y - 4, 8, 8);
          ctx.fillText(t.label, x + 6, y + 3);
        }

        for (let i = 0; i < data.length; i += 32) {
          const r = data[i]! / 255;
          const g = data[i + 1]! / 255;
          const b = data[i + 2]! / 255;
          const u = -0.14713 * r - 0.28886 * g + 0.436 * b;
          const v = 0.615 * r - 0.51499 * g - 0.10001 * b;
          const x = cx + u * rad * 2.2;
          const y = cy - v * rad * 2.2;
          if (x < 0 || y < 0 || x >= w || y >= h) continue;
          ctx.fillStyle = `rgba(${data[i]},${data[i + 1]},${data[i + 2]},0.55)`;
          ctx.fillRect(x, y, 1.2, 1.2);
        }
      }

      raf = requestAnimationFrame(paint);
    };

    raf = requestAnimationFrame(paint);
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
    };
  }, [kind, isPlaying, dualFollower]);

  return (
    <div className="flex min-h-0 min-w-0 flex-col border border-hairline bg-[var(--cg-bg-base)]">
      <div className="flex shrink-0 items-center justify-between border-b border-hairline px-1.5 py-0.5">
        <span className="text-[10px] font-medium text-text-high">{title}</span>
        {dualFollower && isPlaying && (
          <span className="text-[9px] text-text-low">light</span>
        )}
      </div>
      <canvas ref={canvasRef} className="min-h-0 w-full flex-1" />
    </div>
  );
}

/** Resolve-style scopes: Parade / Waveform / Vectorscope / Histogram from live viewer. */
export function ScopesPanel() {
  const selection = useEditorStore((s) => s.selection);
  const [layout, setLayout] = useState<'quad' | 'single'>('quad');
  const [solo, setSolo] = useState<ScopeKind>('parade');

  const scopes: { id: ScopeKind; title: string }[] = [
    { id: 'parade', title: 'Parade' },
    { id: 'waveform', title: 'Waveform' },
    { id: 'vectorscope', title: 'Vectorscope' },
    { id: 'histogram', title: 'Histogram' },
  ];

  return (
    <PanelShell
      title="Scopes"
      actions={
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            title="Single scope"
            className={`px-1 text-[10px] ${layout === 'single' ? 'text-accent' : 'text-text-low'}`}
            onClick={() => setLayout('single')}
          >
            ▢
          </button>
          <button
            type="button"
            title="2×2 scopes"
            className={`px-1 text-[10px] ${layout === 'quad' ? 'text-accent' : 'text-text-low'}`}
            onClick={() => setLayout('quad')}
          >
            ⊞
          </button>
        </div>
      }
      bodyClassName="flex min-h-0 flex-col overflow-hidden p-1"
    >
      {!selection[0] && (
        <p className="mb-1 px-1 text-[10px] text-text-low">
          Select a clip — scopes read the live viewer picture.
        </p>
      )}
      {layout === 'single' && (
        <div className="mb-1 flex gap-1">
          {scopes.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSolo(s.id)}
              className={`border px-1.5 py-0.5 text-[10px] ${
                solo === s.id
                  ? 'border-accent text-accent'
                  : 'border-hairline text-text-low hover:text-text-high'
              }`}
            >
              {s.title}
            </button>
          ))}
        </div>
      )}
      <div
        className={`min-h-0 flex-1 gap-1 ${
          layout === 'quad' ? 'grid grid-cols-2 grid-rows-2' : 'flex'
        }`}
      >
        {(layout === 'quad' ? scopes : scopes.filter((s) => s.id === solo)).map((s) => (
          <ScopeCanvas key={s.id} title={s.title} kind={s.id} />
        ))}
      </div>
    </PanelShell>
  );
}
